import { useCallback, useRef } from 'react';
import { uazapiService } from '../../../../services/uazapiService';
import { updateIntegrationAfterConnect, updateIntegrationStatus } from './integrationRepository';

// ============================================================================
// TYPES
// ============================================================================

interface PollingCallbacks {
    onConnected: () => void;
    onError: (message: string) => void;
}

// ============================================================================
// HOOK
// ============================================================================

export const useConnectionPolling = (callbacks: PollingCallbacks) => {
    const pollingInterval = useRef<NodeJS.Timeout | null>(null);

    const cleanup = useCallback(() => {
        if (pollingInterval.current) {
            clearInterval(pollingInterval.current);
            pollingInterval.current = null;
        }
    }, []);

    /**
     * Verifica o status da conexão com tratamento de erro.
     * Retorna null se a API estiver indisponível (não mata o polling).
     */
    async function safeGetConnectionState(instanceName: string): Promise<string | null> {
        try {
            const status = await uazapiService.getConnectionState(instanceName);
            return status?.instance?.state || null;
        } catch (err) {
            console.warn('[Polling] ⚠️ Erro ao verificar status (API pode estar sobrecarregada):', (err as Error).message);
            return null;
        }
    }

    /**
     * Busca perfil com timeout. Não bloqueia se falhar.
     */
    async function safeGetProfile(instanceName: string): Promise<any> {
        try {
            return await uazapiService.getInstanceProfile(instanceName);
        } catch {
            return null;
        }
    }

    /**
     * Inicia polling após exibir QR Code.
     * Aguarda usuário escanear e conectar.
     * 
     * RESILIÊNCIA: Cada iteração tem try/catch para não matar o polling
     * se o Edge Function estiver temporariamente indisponível.
     */
    const startPollingAfterQRCode = useCallback((
        instanceName: string,
        originalName: string,
        integrationId: string
    ) => {
        if (pollingInterval.current) clearInterval(pollingInterval.current);

        let attempts = 0;
        const maxAttempts = 100; // ~5 minutos

        pollingInterval.current = setInterval(async () => {
            attempts++;

            if (attempts > maxAttempts) {
                cleanup();
                callbacks.onError('Tempo limite excedido. Tente novamente.');
                return;
            }

            try {
                const state = await safeGetConnectionState(instanceName);
                console.log(`[Polling] Status (${attempts}/${maxAttempts}): ${state || 'indisponível'}`);

                // Se API indisponível, continua tentando
                if (!state) return;

                if (state === 'open') {
                    cleanup();

                    // Busca perfil com até 5 tentativas rápidas (não 15!)
                    let profileData = await safeGetProfile(instanceName);
                    let retries = 0;
                    const maxRetries = 5;

                    while (!profileData?.number && retries < maxRetries) {
                        console.log(`[Polling] Tentativa ${retries + 1}/${maxRetries} de buscar perfil...`);
                        await new Promise(r => setTimeout(r, 2000));
                        profileData = await safeGetProfile(instanceName);
                        retries++;
                    }

                    // Atualiza banco (mesmo sem perfil completo)
                    try {
                        await updateIntegrationAfterConnect(
                            integrationId,
                            instanceName,
                            profileData || { name: originalName },
                            originalName
                        );
                    } catch (dbErr) {
                        console.warn('[Polling] ⚠️ Erro ao atualizar banco:', dbErr);
                    }

                    // Setup Webhook (fire-and-forget)
                    uazapiService.setupWebhook(instanceName).catch(err => {
                        console.warn('[Polling] ⚠️ Erro ao configurar webhook:', err);
                    });

                    // Notifica sucesso IMEDIATAMENTE (não espera webhook/profile)
                    callbacks.onConnected();
                }
            } catch (iterationErr) {
                // NUNCA mata o polling — apenas loga e continua
                console.warn(`[Polling] ⚠️ Erro na iteração ${attempts}:`, (iterationErr as Error).message);
            }
        }, 3000);
    }, [cleanup, callbacks]);

    /**
     * Polling para reconexão de instância existente.
     */
    const startPollingForReconnect = useCallback((
        instanceName: string,
        integrationId: string
    ) => {
        if (pollingInterval.current) clearInterval(pollingInterval.current);

        let attempts = 0;
        const maxAttempts = 100;

        pollingInterval.current = setInterval(async () => {
            attempts++;

            if (attempts > maxAttempts) {
                cleanup();
                callbacks.onError('Tempo limite excedido. Tente novamente.');
                return;
            }

            try {
                const state = await safeGetConnectionState(instanceName);
                console.log(`[Reconnect Polling] Status (${attempts}): ${state || 'indisponível'}`);

                if (!state) return;

                if (state === 'open') {
                    cleanup();

                    let profileData = await safeGetProfile(instanceName);
                    let retries = 0;
                    while (!profileData?.number && retries < 5) {
                        await new Promise(r => setTimeout(r, 2000));
                        profileData = await safeGetProfile(instanceName);
                        retries++;
                    }

                    // Atualiza status no banco
                    try {
                        await updateIntegrationStatus(integrationId, 'connected', {
                            instance_name: instanceName,
                            owner_jid: profileData?.number || (profileData as any)?.owner,
                            display_number: (profileData?.number || '').replace('@s.whatsapp.net', '').replace(/\D/g, ''),
                            profile_pic: profileData?.profilePictureUrl
                        });
                    } catch (dbErr) {
                        console.warn('[Reconnect] ⚠️ Erro ao atualizar banco:', dbErr);
                    }

                    // Notifica sucesso imediatamente
                    callbacks.onConnected();
                }
            } catch (iterationErr) {
                console.warn(`[Reconnect Polling] ⚠️ Erro na iteração ${attempts}:`, (iterationErr as Error).message);
            }
        }, 3000);
    }, [cleanup, callbacks]);

    return {
        cleanup,
        startPollingAfterQRCode,
        startPollingForReconnect
    };
};
